import { Box, CardContent, Typography, useTheme } from '@mui/material'
import Card from 'components/@extended/Card'
import CardFooter from 'components/cards/CardFooter'
import dayjs from 'dayjs'
import React, { type FC } from 'react'
import type { UserPeriodOverview } from 'types/dashboard'
import type { Period } from 'types/periods'

interface TeamOverviewProps {
  userPeriodOverviews: UserPeriodOverview[]
  view: 'period' | 'annual'
  selectedPeriod?: string
  periods?: Period[]
  minHeight?: number
}

const TeamOverview: FC<TeamOverviewProps> = ({
  userPeriodOverviews,
  view,
  selectedPeriod = '',
  periods = [],
  minHeight,
}) => {
  const theme = useTheme()

  // Process user data the same way as TeamEmployeeOverviews
  const processedUserData = React.useMemo(() => {
    if (!userPeriodOverviews) return []

    // Helper function to find a period by ID
    const findPeriodById = (periodId: string): Period | undefined => {
      const findInPeriods = (periodsToSearch: Period[]): Period | undefined => {
        for (const period of periodsToSearch) {
          if (period.id === periodId) return period
          if (period.children && period.children.length > 0) {
            const found = findInPeriods(period.children)
            if (found) return found
          }
        }
        return undefined
      }
      return findInPeriods(periods)
    }

    // Helper function to calculate proration ratio for a plan within a period
    const calculateProration = (overview: UserPeriodOverview): number => {
      const period = findPeriodById(overview.periodId)
      if (!period) return overview.periodProration // Fallback to period proration if period not found

      const periodStart = dayjs.utc(period.startDate)
      const periodEnd = dayjs.utc(period.endDate)
      const totalPeriodDays = periodEnd.diff(periodStart, 'day') + 1

      // For backward compatibility: use period dates if plan dates are missing
      const planStartDate = overview.planStartDate ? dayjs.utc(overview.planStartDate) : periodStart
      const planEndDate = overview.planEndDate ? dayjs.utc(overview.planEndDate) : periodEnd

      // Calculate the overlap between plan and period
      const effectiveStart = planStartDate.isAfter(periodStart) ? planStartDate : periodStart
      const effectiveEnd = planEndDate.isBefore(periodEnd) ? planEndDate : periodEnd

      // If there's no overlap, return 0
      if (effectiveEnd.isBefore(effectiveStart)) {
        return 0
      }

      // Calculate how many days the plan was valid in the period
      const planValidDays = effectiveEnd.diff(effectiveStart, 'day') + 1

      // Calculate proration: (planValidDays / totalPeriodDays) * periodProration
      const dateProration = planValidDays / totalPeriodDays

      // Return combined proration ratio
      return dateProration * overview.periodProration
    }

    // Helper function to consolidate multiple plans for the same user
    const consolidateUserPlans = (overviews: UserPeriodOverview[]): UserPeriodOverview[] => {
      const consolidatedUsers = new Map<string, UserPeriodOverview>()

      overviews.forEach((overview) => {
        if (overview.planItems.length === 0) return

        // Calculate proration for this plan (includes both date-based and periodProration)
        const prorationRatio = calculateProration(overview)

        const existingUser = consolidatedUsers.get(overview.userId)
        if (existingUser) {
          // Sum up payout amounts and prorated on-target amounts from all plans
          const combinedPlanItems = existingUser.planItems.map((existingItem, index) => {
            const matchingItem = overview.planItems[index]
            if (matchingItem) {
              const combinedPayoutAmount = existingItem.payoutAmount + matchingItem.payoutAmount
              // Prorate the on-target amount: onTargetAmount * periodProration * (planDays/periodDays)
              const proratedOnTargetAmount = matchingItem.onTargetAmount * prorationRatio
              const combinedOnTargetAmount = existingItem.onTargetAmount + proratedOnTargetAmount

              return {
                ...existingItem,
                payoutAmount: combinedPayoutAmount,
                onTargetAmount: combinedOnTargetAmount,
              }
            }
            return existingItem
          })

          consolidatedUsers.set(overview.userId, {
            ...existingUser,
            planItems: combinedPlanItems,
          })
        } else {
          // First record for this user - apply proration to on-target amounts
          const proratedPlanItems = overview.planItems.map((item) => ({
            ...item,
            onTargetAmount: item.onTargetAmount * prorationRatio,
          }))

          consolidatedUsers.set(overview.userId, {
            ...overview,
            planItems: proratedPlanItems,
          })
        }
      })

      return Array.from(consolidatedUsers.values())
    }

    if (view === 'period') {
      if (!selectedPeriod) return []

      // If the selected period is a root period, show all data
      const isRootPeriod = periods.some((p) => p.id === selectedPeriod)
      const filteredOverviews = isRootPeriod
        ? userPeriodOverviews.filter((overview) => overview.planItems.length > 0)
        : userPeriodOverviews.filter(
            (overview) => overview.periodId === selectedPeriod && overview.planItems.length > 0
          )
      return consolidateUserPlans(filteredOverviews)
    } else if (view === 'annual') {
      // Annual Progress: Sum payout from all periods (across different period IDs)
      const allValidOverviews = userPeriodOverviews.filter((overview) => overview.planItems.length > 0)
      return consolidateUserPlans(allValidOverviews)
    } else {
      // Default: consolidate all users with plan items
      const allValidOverviews = userPeriodOverviews.filter((overview) => overview.planItems.length > 0)
      return consolidateUserPlans(allValidOverviews)
    }
  }, [userPeriodOverviews, view, selectedPeriod, periods])

  const calculateTotals = () => {
    let totalOnTargetAmount = 0
    let totalPayoutAmount = 0

    processedUserData.forEach((user) => {
      user.planItems.forEach((item) => {
        // OnTargetAmount is already prorated during consolidation
        totalOnTargetAmount += item.onTargetAmount
        totalPayoutAmount += item.payoutAmount
      })
    })

    return { totalOnTargetAmount, totalPayoutAmount }
  }

  const { totalOnTargetAmount, totalPayoutAmount } = calculateTotals()

  const formattedTotalPayout = totalPayoutAmount.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const formattedTotalOnTarget = totalOnTargetAmount.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  const totalColor = theme.palette.grey[400]

  return (
    <Card variant="outlined" sx={{ minHeight, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
        <Box sx={{ mb: 1, ml: { xs: 1, sm: 3 } }}>
          <Typography sx={{ fontWeight: '500', fontSize: { xs: 16, sm: 18 }, mt: 1 }}>
            Total Variable Compensation Earned:
          </Typography>
          <Typography variant="h2" sx={{ fontWeight: '700', fontSize: { xs: '2rem', sm: '3rem' }, mt: 1 }}>
            ${formattedTotalPayout}
          </Typography>
        </Box>
      </CardContent>
      <CardFooter>
        <Box sx={{ ml: { xs: 2, sm: 5 } }}>
          <Typography sx={{ color: totalColor, fontWeight: 'bold', fontSize: { xs: 12, sm: 14 } }}>
            {`Up to $${formattedTotalOnTarget} (On-Target Variable Compensation)`}
          </Typography>
        </Box>
      </CardFooter>
    </Card>
  )
}

export default TeamOverview
